import React from "react";
import { Link } from "react-router-dom";
import { 
  Envelope, 
  ChartLine, 
  Globe, 
  Users, 
  Newspaper, 
  TrendUp,
  CheckCircle,
  ArrowRight
} from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

export default function AdvertisePage() {
  const audienceStats = [
    { label: "Monthly Readers", value: "Growing", icon: Users },
    { label: "Countries Covered", value: "8", icon: Globe },
    { label: "Articles Published", value: "500+", icon: Newspaper },
    { label: "Engagement Rate", value: "4.2%", icon: TrendUp },
  ];

  const advertisingOptions = [
    {
      title: "Website Display Ads",
      description: "High-visibility banner placements across our site",
      options: [
        "Homepage banner - Premium visibility",
        "In-article placements - Contextual relevance",
        "Category sponsorship - Targeted reach",
        "Sidebar presence - Persistent exposure"
      ],
      price: "From €750/month"
    },
    {
      title: "Sponsored Content",
      description: "Thought leadership and branded articles",
      options: [
        "Branded articles - Your story, our platform",
        "Product features - Showcase your solutions",
        "Case studies - Real-world impact stories",
        "Expert columns - Position your brand as a leader"
      ],
      price: "From €800/article"
    },
    {
      title: "Newsletter Sponsorship",
      description: "Direct access to our engaged subscriber base",
      options: [
        "Weekly Farmwatch Insights - High open rates",
        "Dedicated send - Exclusive focus",
        "Banner placement - Consistent visibility",
        "Content integration - Native feel"
      ],
      price: "From €250/send"
    },
    {
      title: "Category Sponsorship",
      description: "Own a content vertical",
      options: [
        "Markets section sponsor",
        "Ireland focus sponsor",
        "Technology sponsor",
        "Exclusive category branding"
      ],
      price: "Custom pricing"
    }
  ];

  const audienceProfile = [
    { segment: "Farm Owners & Operators", percentage: 45 },
    { segment: "Agribusiness Professionals", percentage: 25 },
    { segment: "Agricultural Investors", percentage: 15 },
    { segment: "Policy & Industry Stakeholders", percentage: 10 },
    { segment: "Agricultural Students & Researchers", percentage: 5 },
  ];

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead 
        title="Advertise With Us - Media Pack"
        url="/advertise"
        description="Reach decision-makers in global agriculture. Farmwatch connects you with farmers, agribusiness professionals, and agricultural investors across Ireland, UK, USA, and beyond."
        keywords="farmwatch advertising, agriculture advertising, farming media, agribusiness marketing"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#022C22] via-[#064E3B] to-[#0F766E] text-white py-16 sm:py-24">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="max-w-3xl">
              <span className="inline-block px-3 py-1 bg-white/10 text-[#5EEAD4] text-xs font-mono uppercase tracking-wider mb-4">
                Media Pack
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
                Reach Decision-Makers in Global Agriculture
              </h1>
              <p className="text-lg sm:text-xl text-[#A7F3D0] leading-relaxed mb-8">
                Farmwatch connects you with farmers, agribusiness professionals, and agricultural investors 
                across Ireland, UK, USA, and global markets. Our audience is commercially focused and 
                actively engaged in purchasing and investment decisions.
              </p>
              <a 
                href="mailto:advertise@farmwatch.com"
                data-testid="hero-contact-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#064E3B] font-semibold hover:bg-[#F0FDF4] transition-colors"
              >
                <Envelope size={20} />
                Get in Touch
              </a>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-b border-[#E0D4C8]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {audienceStats.map((stat) => (
                <div key={stat.label} className="text-center p-6">
                  <stat.icon size={32} className="mx-auto text-[#0F766E] mb-3" />
                  <div className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-[#6B6661] font-sans">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Farmwatch */}
        <section className="py-16 sm:py-20">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-6">
                  About Farmwatch
                </h2>
                <p className="text-[#6B6661] leading-relaxed mb-4">
                  Farmwatch is a digital platform focused on agriculture, markets, and global food systems. 
                  We provide insight into global agriculture trends, analysis of farming economics, and 
                  coverage of Irish and international agri-business.
                </p>
                <p className="text-[#6B6661] leading-relaxed mb-6">
                  Our positioning is unique: <strong>"Where agriculture meets economics, markets, and global strategy."</strong> 
                  This separates us from traditional farm news outlets and creates a premium environment for advertisers.
                </p>
                <div className="space-y-3">
                  {[
                    "Authoritative, FT-style editorial approach",
                    "Focus on commercial and economic angles",
                    "Global perspective with strong local relevance",
                    "Decision-maker audience, not casual browsers"
                  ].map((point, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                      <span className="text-[#6B6661]">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#F9FAFB] border border-[#E0D4C8] p-8">
                <h3 className="font-serif text-xl text-[#33302E] mb-6">Audience Profile</h3>
                <div className="space-y-4">
                  {audienceProfile.map((item) => (
                    <div key={item.segment}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#6B6661]">{item.segment}</span>
                        <span className="font-semibold text-[#33302E]">{item.percentage}%</span>
                      </div>
                      <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#0F766E] to-[#5EEAD4]"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Categories */}
        <section className="py-16 sm:py-20 bg-[#F9FAFB]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-4">
                Content Categories
              </h2>
              <p className="text-[#6B6661] max-w-2xl mx-auto">
                Our content spans the full agricultural value chain, giving advertisers multiple 
                touchpoints to reach their target audience.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { 
                  title: "Global Agriculture", 
                  topics: ["Countries", "Commodities", "Trade flows", "Supply chains"],
                  icon: "🌍"
                },
                { 
                  title: "Ireland Focus", 
                  topics: ["Dairy", "Livestock", "Policy", "Companies"],
                  icon: "🇮🇪"
                },
                { 
                  title: "Markets & Prices", 
                  topics: ["Commodity prices", "Factory prices", "Mart reports", "Trends"],
                  icon: "📊"
                },
                { 
                  title: "Analysis", 
                  topics: ["Long-form insights", "Strategic themes", "Economic analysis", "Forecasts"],
                  icon: "🧠"
                },
              ].map((category) => (
                <div key={category.title} className="bg-white border border-[#E0D4C8] p-6 hover:border-[#0F766E] transition-colors">
                  <div className="text-3xl mb-4">{category.icon}</div>
                  <h3 className="font-serif text-lg text-[#33302E] mb-3">{category.title}</h3>
                  <ul className="space-y-2">
                    {category.topics.map((topic) => (
                      <li key={topic} className="text-sm text-[#6B6661] flex items-center gap-2">
                        <span className="w-1 h-1 bg-[#0F766E] rounded-full" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advertising Options */}
        <section className="py-16 sm:py-20">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-4">
                Advertising Opportunities
              </h2>
              <p className="text-[#6B6661] max-w-2xl mx-auto">
                Multiple formats to suit your marketing objectives and budget.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {advertisingOptions.map((option) => (
                <div 
                  key={option.title}
                  className="bg-white border border-[#E0D4C8] p-8 hover:border-[#0F766E] transition-colors"
                >
                  <h3 className="font-serif text-xl text-[#33302E] mb-2">{option.title}</h3>
                  <p className="text-[#6B6661] text-sm mb-4">{option.description}</p>
                  <ul className="space-y-2 mb-6">
                    {option.options.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-[#6B6661]">
                        <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-[#E0D4C8]">
                    <span className="text-[#0F766E] font-semibold">{option.price}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Selling Point */}
        <section className="py-16 sm:py-20 bg-[#022C22] text-white">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <ChartLine size={48} className="mx-auto text-[#5EEAD4] mb-6" />
              <h2 className="font-serif text-3xl sm:text-4xl mb-6">
                Why Advertise with Farmwatch?
              </h2>
              <p className="text-xl text-[#A7F3D0] leading-relaxed mb-8">
                "Farmwatch reaches a commercially focused agriculture audience that is 
                actively engaged in decision-making, purchasing, and investment."
              </p>
              <div className="grid sm:grid-cols-3 gap-6 mt-12">
                {[
                  { stat: "4x", label: "Higher engagement than industry average" },
                  { stat: "72%", label: "Readers are business decision-makers" },
                  { stat: "85%", label: "Return visitor rate" },
                ].map((item) => (
                  <div key={item.label} className="p-6 bg-white/5 border border-white/10">
                    <div className="font-serif text-4xl text-[#5EEAD4] mb-2">{item.stat}</div>
                    <div className="text-sm text-[#A7F3D0]">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20 bg-[#F9FAFB] border-t border-[#E0D4C8]">
          <div className="max-w-[800px] mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-4">
              Ready to Reach Our Audience?
            </h2>
            <p className="text-[#6B6661] mb-8">
              Contact us to discuss your advertising goals and receive a customised proposal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="mailto:advertise@farmwatch.com"
                data-testid="cta-email-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F766E] text-white font-semibold hover:bg-[#064E3B] transition-colors"
              >
                <Envelope size={20} />
                advertise@farmwatch.com
              </a>
              <Link
                to="/"
                data-testid="cta-explore-btn"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#E0D4C8] text-[#6B6661] hover:border-[#0F766E] hover:text-[#0F766E] transition-colors"
              >
                Explore Our Content
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
